import { useMemo } from "react";
import { TopTenResponse } from "../../../pages/inSeasonReviewPage/hooks/useFetchTopTen";

/**
 * Hook to calculate the hit rate between sales and purchase top ten rankings.
 *
 * Hit Rate is defined as the percentage of products that appear in both rankings
 * relative to the total number of products in the sales ranking.
 *
 * @param salesRowData - Row data for sales top ten ranking
 * @param purchaseRowData - Row data for purchase top ten ranking
 * @param isLoading - Loading state for both tables
 * @returns Hit rate as a number between 0 and 100
 */

export function useCalculateHitRate(
  salesRowData: TopTenResponse[],
  purchaseRowData: TopTenResponse[],
  isLoading: boolean
): number {
  return useMemo(() => {
    if (isLoading) {
      return 0;
    }
    if (!salesRowData?.length || !purchaseRowData?.length) {
      return 0;
    }

    const productIdsFromRankingA = salesRowData.map(
      (row) => row.style_option_id_mockup
    );

    const productIdsFromRankingB = purchaseRowData.map(
      (row) => row.style_option_id_mockup
    );

    if (
      productIdsFromRankingA.length === 0 ||
      productIdsFromRankingB.length === 0
    ) {
      return 0;
    }

    const productsAppearingInBoth = productIdsFromRankingA.filter((id) =>
      productIdsFromRankingB.includes(id)
    ).length;

    const hitRate =
      (productsAppearingInBoth / productIdsFromRankingA.length) * 100;

    return hitRate;
  }, [isLoading, salesRowData, purchaseRowData]);
}
